import { APIError } from "./api";

// humanizeError turns any thrown error into an ordinary, user-friendly
// message. It NEVER surfaces raw backend strings, status codes, or
// technical detail: a known error code maps to a written sentence, a
// known HTTP status maps to a generic sentence, and everything else falls
// back to the caller-supplied message. Callers always pass a fallback
// that reads well on its own (e.g. "Couldn't connect the server.").

// Known machine-readable codes from the API → friendly copy.
const CODE_MESSAGES: Record<string, string> = {
  reauth_required: "Your authorization for this integration expired. Reconnect it to continue.",
  server_in_use: "This integration is still used by one or more projects. Remove it from those projects first.",
  update_in_progress: "An update is already running for this project. Please wait for it to finish.",
  not_found: "We couldn't find that — it may have been removed.",
  not_connected: "This integration isn't connected yet. Finish connecting it, then try again.",
  subdomain_taken: "That name is already taken. Please choose a different one.",
  domain_taken: "That domain is already claimed. Try a different one.",
  insufficient_role: "You don't have permission to do that.",
  quota_exceeded: "You've reached a plan limit. Remove something or upgrade to continue.",
  plan_limit: "That isn't included in your current plan. Upgrade to unlock it.",
  storage_full: "There isn't enough storage left. Remove some files and try again.",
  too_large: "That file is too large.",
  // two-factor
  invalid_code: "That code didn't match. Check your authenticator app and try again.",
  too_many_attempts: "Too many attempts. Please wait a few minutes and try again.",
  challenge_expired: "This verification expired. Please sign in again.",
  totp_already_enabled: "Two-step verification is already turned on.",
  totp_not_started: "Start the setup first, then enter a code.",
  totp_not_enabled: "Two-step verification isn't turned on.",
  two_factor_unavailable: "Two-step verification isn't available right now. Please try again later.",
  session_required: "Please sign in to manage two-step verification.",
};

// HTTP status → friendly copy (used when there's no specific code).
const STATUS_MESSAGES: Record<number, string> = {
  400: "Some of the details look off. Please review and try again.",
  401: "Your session expired. Please sign in again.",
  402: "That isn't included in your current plan. Upgrade to unlock it.",
  403: "You don't have permission to do that.",
  404: "We couldn't find that — it may have been removed.",
  409: "That conflicts with something that already exists.",
  413: "That's too large to upload.",
  429: "Too many requests right now. Please wait a moment and try again.",
  500: "Something went wrong on our end. Please try again.",
  502: "We're having trouble reaching the service. Please try again shortly.",
  503: "The service is busy right now. Please try again in a moment.",
  504: "The request took too long. Please try again.",
  507: "There isn't enough storage left. Remove some files and try again.",
};

function normalizeCode(s: string | undefined): string | undefined {
  if (!s) return undefined;
  // Treat short snake_case-ish tokens as codes (e.g. "server_in_use"); a
  // full sentence with spaces is not a code.
  const t = s.trim().toLowerCase();
  return /^[a-z][a-z0-9_]{2,40}$/.test(t) ? t : undefined;
}

export function humanizeError(e: unknown, fallback = "Something went wrong. Please try again."): string {
  if (e instanceof APIError) {
    const code = normalizeCode(e.code) ?? normalizeCode(e.message);
    if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
    if (STATUS_MESSAGES[e.status]) return STATUS_MESSAGES[e.status];
    return fallback;
  }
  // A fetch/network failure surfaces as a TypeError.
  if (e instanceof TypeError) {
    return "We couldn't reach the service. Check your connection and try again.";
  }
  return fallback;
}

// isStatus is a small helper for views that still need to branch on a
// specific status (e.g. show a different empty state on 403) without
// re-importing APIError everywhere.
export function isStatus(e: unknown, status: number): boolean {
  return e instanceof APIError && e.status === status;
}
